import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import RelatedLinks from '../components/RelatedLinks';
import Modal from '../components/Modal';
import Tabs from '../components/Tabs';
import { useModal } from '../components/useModal';

/**
 * Confirmation email detail.
 *
 * The body is the reason this page exists — somebody is here to read what the
 * venue actually wrote — so it is *on* the page in a tab strip, in all four
 * variants the record stores. The old page hid each behind a link that opened a
 * modal with an iframe, which meant four clicks to compare the raw html against
 * what the parser cleaned out of it.
 *
 * The html variants still render in an iframe, because that is the only way to
 * show a provider's markup without it inheriting this page's styles or reaching
 * into it. `sandbox=""` — no `allow-scripts`, no same-origin — so nothing in a
 * received email runs. (The old page passed `allow-presentation`, which is not
 * dangerous, but neither is it needed.)
 *
 * All three actions are carried over, with one correction noted below.
 */

// `Api.WannaSport/Model/WannaSport/RelayType.cs`. Kept here rather than in
// `src/enums/` because nothing in the old admin exports it, and this tree does
// not edit files the old admin also reads.
const RELAY_TYPE = [
  'Unknown',
  'External manual',
  'External manual skip',
  'External auto',
  'External auto skip',
  'Internal manual',
  'Internal auto email',
  'Internal auto concierge',
  'Internal direct activity',
  'Fake'
];

const BODIES = [
  { key: 'html', label: 'Html', field: 'html', html: true },
  { key: 'cleanedHtml', label: 'Cleaned html', field: 'cleanedHtml', html: true },
  { key: 'text', label: 'Text', field: 'text' },
  { key: 'cleanedText', label: 'Cleaned text', field: 'cleanedText' }
];

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const YesNo = ({ value }) => {
  if (value === null || value === undefined) return <span className="next-muted">—</span>;
  return (
    <span className={`next-pill next-pill--${value ? 'ok' : 'neutral'}`}>
      {value ? 'Yes' : 'No'}
    </span>
  );
};

YesNo.propTypes = { value: PropTypes.bool };

const EmailInternal = ({ api }) => {
  const { emailId } = useParams();
  const { ask, modalProps } = useModal();

  const [email, setEmail] = useState(null);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState(null);
  const [tab, setTab] = useState('text');
  const [relay, setRelay] = useState(null);
  const [relayRunning, setRelayRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.confirmationEmail.getConfirmationById(emailId);
      // The endpoint answers the record itself, and `{ success: false }` when it
      // could not read it — so a missing `id` is the failure, not an exception.
      if (!data || data.success === false || !data.id) {
        setFailed(true);
        return;
      }
      setEmail(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, emailId]);

  useEffect(() => {
    load();
  }, [load]);

  const say = (tone, text) => setNote({ tone, text });

  const ctx = `email …${String(emailId).slice(-6)}`;

  /**
   * Confirm this email against a signup.
   *
   * `forceIsBooking` decides the outcome in `ConfirmationEmailLogic.ProcessEmail`:
   * `isAttending = forceIsBooking`, `isCancelled = !forceIsBooking`. The old page
   * offers both actions but its dialog calls `confirmEmail(true)` in both cases,
   * so its "Confirm Cancellation" marks the signup as *attending* — the opposite
   * of its label. Here the value follows the action, and the dialog states the
   * outcome before you commit to it. Flagged in REWRITE-STATE.md; the old page is
   * not touched.
   */
  const confirm = isBooking => ask({
    kind: 'input',
    title: isBooking ? 'Confirm as a booking' : 'Confirm as a cancellation',
    context: `${isBooking ? 'Confirm signup' : 'Confirm cancellation'} · ${ctx}`,
    body: isBooking
      ? 'The signup you name is marked as attending and this email is attached to it as its confirmation.'
      : 'The signup you name is marked as cancelled and this email is attached to it as its cancellation.',
    label: 'Signup id',
    placeholder: '00000000-0000-0000-0000-000000000000',
    hint: 'The full UUID of the signup this email confirms',
    mono: true,
    warning: 'The email can only be attached once — after this the two actions are closed for it.',
    confirmLabel: isBooking ? 'Confirm booking' : 'Confirm cancellation',
    onConfirm: async value => {
      const result = await api.confirmationEmail.confirmSignup(email.id, value.trim(), isBooking);
      const ok = result?.success === true;
      say(ok ? 'info' : 'error', result?.message || (ok ? 'Attached.' : 'Unable to attach this email.'));
      await load();
      // Thrown so `useModal` does not report the dialog as having succeeded.
      if (!ok) throw new Error('confirm failed');
    }
  });

  const testRelay = async () => {
    setRelayRunning(true);
    setRelay(null);
    try {
      const result = await api.confirmationEmail.relayRun(email.id);
      if (result?.success === false) {
        say('error', 'The relay test did not run.');
      } else {
        setRelay(result?.data ?? null);
      }
    } finally {
      setRelayRunning(false);
    }
  };

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/emails">← All emails</BackToList>
        <div className="next-note next-note--error">
          Could not load this email.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!email) return <div className="next-page next-muted">Loading…</div>;

  // Already attached to something, so there is nothing left to confirm — the old
  // page greys both actions out on the same condition.
  const attached = !!email.signupId || !!email.seasonalCourtPurchaseId;
  // A relay rule is matched against a *received* email; running it on one we sent
  // tells you nothing.
  const ourOwn = String(email.from || '').includes('wannasport');

  const recipients = String(email.to || '').split(',').map(value => value.trim()).filter(Boolean);
  const dirtyWords = Array.isArray(email.dirtyWords) ? email.dirtyWords : [];

  const facts = [
    ['Signup id', email.signupId
      ? <Guid value={email.signupId} to={`/next/signup/${email.signupId}`} />
      : <span className="next-muted">—</span>],
    ['Created date (CET)', fmtDate(email.createdDate)],
    ['Relay type', RELAY_TYPE[email.relayType] || `Unknown (${email.relayType})`],
    // Copyable but not linked: the old admin's `/relayRule` is a list with no
    // per-rule route, so a link here would land on a 404.
    ['Relay rule id', email.relayRuleId
      ? <Guid value={email.relayRuleId} />
      : <span className="next-muted">—</span>],
    ['Seasonal court purchase id', email.seasonalCourtPurchaseId
      ? <Guid value={email.seasonalCourtPurchaseId} href={`/seasonalcourtpurchase/${email.seasonalCourtPurchaseId}`} />
      : <span className="next-muted">—</span>],
    ['Is read', <YesNo value={email.isRead} />],
    ['Is external email', <YesNo value={email.isExternalEmail} />],
    ['Is attending', <YesNo value={email.isAttending} />],
    ['Mute', <YesNo value={email.mute} />],
    ['Is dirty', <YesNo value={email.isDirty} />],
    ['Dirty words', dirtyWords.length
      ? <span className="next-mono-list">{dirtyWords.join(', ')}</span>
      : <span className="next-muted">—</span>]
  ];

  const active = BODIES.find(({ key }) => key === tab) || BODIES[0];
  const body = email[active.field];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/emails">← All emails</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>Confirmation email</h1>
          <span className="next-pill next-pill--info">
            {RELAY_TYPE[email.relayType] || 'Unknown relay type'}
          </span>
        </div>
        <span className="next-detail__id">{email.id}</span>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card next-envelope">
            <div className="next-envelope__row">
              <div className="next-envelope__party">
                <span className="next-fact__label">From</span>
                <span className="next-envelope__address">{email.from || '—'}</span>
              </div>
              <span className="next-envelope__arrow" aria-hidden>→</span>
              <div className="next-envelope__party">
                <span className="next-fact__label">To</span>
                {recipients.length
                  ? recipients.map(value => (
                    <span className="next-envelope__address" key={value}>{value}</span>
                  ))
                  : <span className="next-muted">—</span>}
              </div>
            </div>
            <div className="next-envelope__subject">
              <span className="next-fact__label">Subject</span>
              <strong>{email.subject || '—'}</strong>
            </div>
          </section>

          <section className="next-card">
            <div className="next-facts">
              {facts.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="next-card">
            {/* The count is the length in characters, which is the only thing
                worth knowing before opening a variant: 0 means the parser kept
                nothing, and that is usually the answer somebody came for. */}
            <Tabs
              tabs={BODIES.map(({ key, label, field }) => ({
                key,
                label,
                count: (email[field] || '').length
              }))}
              active={tab}
              onChange={setTab}
            />

            {body
              ? (active.html
                ? (
                  <iframe
                    className="next-emailbody"
                    title={active.label}
                    sandbox=""
                    srcDoc={body}
                  />
                )
                : <pre className="next-pre next-emailbody__text">{body}</pre>)
              : (
                <p className="next-table__note next-muted">
                  This email has no {active.label.toLowerCase()}.
                </p>
              )}
          </section>

          {relay && (
            <section className="next-card">
              <div className="next-card__head">
                <h2>Relay test</h2>
                <span className="next-muted">not applied — this only reports what a run would find</span>
              </div>
              <div className="next-facts next-facts--single">
                {/* An empty GUID is what the endpoint returns when it caught an
                    exception, so it is reported as no rule rather than as one. */}
                <div className="next-fact">
                  <span className="next-fact__label">Matched rule</span>
                  <span className="next-fact__value">
                    {relay.ruleId && relay.ruleId !== '00000000-0000-0000-0000-000000000000'
                      ? <Guid value={relay.ruleId} />
                      : <span className="next-muted">none</span>}
                  </span>
                </div>
              </div>
              <pre className="next-pre">{relay.explanation || 'The run returned no explanation.'}</pre>
            </section>
          )}
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Actions</h2></div>
            <div className="next-actionlist">
              <button
                type="button"
                className="next-actionlist__item"
                disabled={attached}
                title={attached ? 'This email is already attached to a signup or a seasonal court purchase' : undefined}
                onClick={() => confirm(true)}
              >
                Confirm signup
              </button>
              <button
                type="button"
                className="next-actionlist__item"
                disabled={attached}
                title={attached ? 'This email is already attached to a signup or a seasonal court purchase' : undefined}
                onClick={() => confirm(false)}
              >
                Confirm cancellation
              </button>
              <button
                type="button"
                className="next-actionlist__item"
                disabled={ourOwn || relayRunning}
                title={ourOwn ? 'Relay rules match emails we received, and this one we sent' : undefined}
                onClick={testRelay}
              >
                {relayRunning ? 'Testing the relay…' : 'Test the relay'}
              </button>
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              empty="Not attached to anything."
              items={[
                email.signupId && {
                  label: 'Signup',
                  hint: 'what this email confirms',
                  to: `/next/signup/${email.signupId}`
                },
                email.seasonalCourtPurchaseId && {
                  label: 'Seasonal court purchase',
                  href: `/seasonalcourtpurchase/${email.seasonalCourtPurchaseId}`
                },
                // The searches somebody runs next, already filtered: everything
                // this venue sent, and the rest of the thread for this booking.
                email.from && {
                  label: 'From this sender',
                  hint: email.from,
                  to: `/next/emails?sender=${encodeURIComponent(email.from)}`
                },
                email.signupId && {
                  label: 'All emails for this signup',
                  to: `/next/emails?signupId=${email.signupId}`
                },
                email.subject && {
                  label: 'Same subject',
                  hint: 'other copies of this mail',
                  to: `/next/emails?subject=${encodeURIComponent(email.subject.slice(0, 60))}`
                }
              ]}
            />
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

EmailInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Email = withApi(EmailInternal);

Email.requiresLogin = true;

export default Email;
